import { Link } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowRight,
  Bell,
  CheckCheck,
  CheckCircle,
  CircleDot,
  Info,
  Trash2,
  TriangleAlert,
} from 'lucide-react'
import { useState } from 'react'

import {
  NotificationLevel,
  NotificationStatus,
  UserRole,
} from '@brickup/shared/enums'
import { NOTIFICATIONS } from '@brickup/shared/locales/de/notifications'
import type { Notification } from '@brickup/shared/models'
import { formatDateTime } from '@brickup/shared/utils'
import {
  useDeleteNotification,
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  useToggleNotificationStatus,
  useUnreadCount,
} from '@/api/notifications'
import {
  Button,
  cn,
  LoadingIndicator,
  Popover,
  SECTION_TITLE_CLASS,
  TONE_INK,
} from '@/components/base'
import { EmptyState } from '@/components/EmptyState'
import { LinkButton } from '@/components/LinkButton'
import { selectUserRoles, useAppStore } from '@/stores'
import { asLink } from '@/utils/runtime-route'

const PAGE_SIZE = 50

const LEVEL_ICON = {
  [NotificationLevel.INFO]: Info,
  [NotificationLevel.SUCCESS]: CheckCircle,
  [NotificationLevel.WARNING]: TriangleAlert,
  [NotificationLevel.ERROR]: AlertCircle,
} as const

const LEVEL_INK = {
  [NotificationLevel.INFO]: TONE_INK.info,
  [NotificationLevel.SUCCESS]: TONE_INK.success,
  [NotificationLevel.WARNING]: TONE_INK.warning,
  [NotificationLevel.ERROR]: TONE_INK.error,
} as const

/** Where a notification points, when it points anywhere. */
type NotificationAction = { redirect?: string }

/** What the message wore as a button and wears as a link. */
const MESSAGE_CLASS =
  'min-w-0 flex-1 break-words pr-2 text-left text-sm font-medium text-foreground-2'
const UNREAD_CLASS = 'font-semibold text-foreground'

/**
 * What the system has to say to staff, and how much of it is unread.
 *
 * @single-use one of the global controls, mounted once by the page header
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { data: unread, refetch } = useUnreadCount()
  const userRoles = useAppStore(selectUserRoles)

  if (userRoles?.includes(UserRole.CUSTOMER)) return null

  const unreadCount = unread?.count ?? 0

  const close = () => {
    setOpen(false)
    // The list marks rows read as they are opened, so the badge is stale by
    // the time the popover shuts.
    void refetch()
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => (next ? setOpen(true) : close())}
      align="end"
      className="w-96 p-0"
      trigger={
        <Button
          variant="ghost"
          size="icon"
          aria-label={NOTIFICATIONS.ariaLabel}
          className="relative"
        >
          <Bell size={16} />
          {unreadCount > 0 ? (
            <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-bold leading-none text-primary-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </Button>
      }
    >
      <NotificationsList onClose={close} />
    </Popover>
  )
}

function NotificationsList({ onClose }: { onClose: () => void }) {
  const { data: notifications = [], isLoading } = useNotifications({
    take: PAGE_SIZE,
  })
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const deleteNotification = useDeleteNotification()
  const toggleStatus = useToggleNotificationStatus()

  const hasUnread = notifications.some(
    notification => notification.status === NotificationStatus.UNREAD,
  )

  /**
   * What following a notification does besides going there.
   *
   * `Link` owns the press: it keeps the href for the middle button and
   * ctrl-click, and navigates in this window without a reload. This only marks
   * the row read and shuts the panel behind it.
   */
  const follow = (notification: Notification) => {
    if (notification.status === NotificationStatus.UNREAD) {
      markAsRead.mutate(notification.id)
    }
    onClose()
  }

  return (
    <div className="flex max-h-[600px] flex-col">
      <div className="flex shrink-0 flex-col gap-2 border-b border-border p-4">
        <span className={SECTION_TITLE_CLASS}>{NOTIFICATIONS.title}</span>
        {hasUnread && !isLoading ? (
          <Button
            size="sm"
            variant="outline"
            icon={CheckCheck}
            className="self-start"
            isLoading={markAllAsRead.isPending}
            onClick={() => markAllAsRead.mutate()}
          >
            {NOTIFICATIONS.markAllAsRead}
          </Button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? <LoadingIndicator layout="inline" /> : null}

        {!isLoading && notifications.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={Info}
              title={NOTIFICATIONS.empty.title}
              description={NOTIFICATIONS.empty.description}
            />
          </div>
        ) : null}

        {notifications.map(notification => {
          const Glyph = LEVEL_ICON[notification.level]
          const isUnread = notification.status === NotificationStatus.UNREAD
          const redirect = (notification.action as NotificationAction | null)
            ?.redirect

          return (
            <div
              key={notification.id}
              className={cn(
                'flex flex-row gap-3 border-b border-line-soft p-4',
                isUnread && 'bg-info-soft',
              )}
            >
              <span className="flex w-3 shrink-0 justify-center pt-1.5">
                {isUnread ? (
                  <span className="h-2 w-2 rounded-full bg-info" />
                ) : null}
              </span>

              <Glyph
                size={20}
                className={cn('mt-1 shrink-0', LEVEL_INK[notification.level])}
                aria-hidden
              />

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-row items-start gap-2">
                  {/* The whole message is the target where there is one, so a
                      press anywhere on it goes where the button below goes.
                      Unchanged to look at: the anchor wears what the button
                      wore. */}
                  {redirect ? (
                    <Link
                      {...asLink(redirect)}
                      onClick={() => follow(notification)}
                      className={cn(MESSAGE_CLASS, isUnread && UNREAD_CLASS)}
                    >
                      {notification.message}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        isUnread && markAsRead.mutate(notification.id)
                      }
                      className={cn(MESSAGE_CLASS, isUnread && UNREAD_CLASS)}
                    >
                      {notification.message}
                    </button>
                  )}

                  <span className="flex shrink-0 flex-row gap-1">
                    <Button
                      tooltip={
                        isUnread
                          ? NOTIFICATIONS.markAsRead
                          : NOTIFICATIONS.markAsUnread
                      }
                      size="icon"
                      variant="ghost"
                      icon={CircleDot}
                      className={isUnread ? 'text-info' : undefined}
                      onClick={() =>
                        toggleStatus.mutate({
                          id: notification.id,
                          data: {
                            status: isUnread
                              ? NotificationStatus.READ
                              : NotificationStatus.UNREAD,
                          },
                        })
                      }
                    />
                    <Button
                      tooltip={NOTIFICATIONS.delete}
                      size="icon"
                      variant="ghost"
                      icon={Trash2}
                      onClick={() => deleteNotification.mutate(notification.id)}
                    />
                  </span>
                </div>

                <span className="mt-2 text-xs text-faint">
                  {formatDateTime(notification.createdAt)}
                </span>

                {redirect ? (
                  <LinkButton
                    {...asLink(redirect)}
                    variant="outline"
                    size="sm"
                    className="mt-3 self-start"
                    onClick={() => follow(notification)}
                  >
                    {NOTIFICATIONS.viewDetails}
                    <ArrowRight size={14} aria-hidden />
                  </LinkButton>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
